import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function buildIceConfig() {
  const forceRelay = process.env.REACT_APP_EXTERNALINTERVIEW_FORCE_TURN_ONLY === "true";

  return {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      {
        urls: [
          "turn:3.111.62.152:3478",
          "turn:3.111.62.152:3478?transport=tcp",
        ],
        username: process.env.REACT_APP_TURN_USERNAME || "resourceful_turn",
        credential: process.env.REACT_APP_TURN_CREDENTIAL || "resourceful_turn_password",
      },
    ],
    iceTransportPolicy: forceRelay ? "relay" : "all",
    bundlePolicy: "max-bundle",
    rtcpMuxPolicy: "require",
  };
}

function stopMediaStream(stream) {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
}

function stopMirroredVideoPipeline(pipeline) {
  if (!pipeline) {
    return;
  }

  if (pipeline.animationFrameId) {
    cancelAnimationFrame(pipeline.animationFrameId);
  }

  if (pipeline.videoElement) {
    pipeline.videoElement.pause();
    pipeline.videoElement.srcObject = null;
  }

  if (pipeline.canvasStream) {
    pipeline.canvasStream.getTracks().forEach((track) => track.stop());
  }
}

async function createMirroredVideoTrack(stream) {
  const [videoTrack] = stream.getVideoTracks();
  if (!videoTrack) {
    return { mirroredVideoTrack: null, cleanup: null };
  }

  const videoElement = document.createElement("video");
  videoElement.autoplay = true;
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.srcObject = new MediaStream([videoTrack]);

  try {
    await videoElement.play();
  } catch (error) {
    // Some browsers resolve dimensions after metadata is ready even if play() is blocked.
  }

  await new Promise((resolve) => {
    if (videoElement.readyState >= 1) {
      resolve();
      return;
    }
    videoElement.onloadedmetadata = () => resolve();
  });

  const settings = videoTrack.getSettings();
  const width = settings.width || videoElement.videoWidth || 1280;
  const height = settings.height || videoElement.videoHeight || 720;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context || typeof canvas.captureStream !== "function") {
    return { mirroredVideoTrack: videoTrack, cleanup: null };
  }

  const pipeline = {
    animationFrameId: null,
    canvasStream: null,
    videoElement,
  };

  const drawFrame = () => {
    context.save();
    context.clearRect(0, 0, width, height);
    context.translate(width, 0);
    context.scale(-1, 1);
    context.drawImage(videoElement, 0, 0, width, height);
    context.restore();
    pipeline.animationFrameId = requestAnimationFrame(drawFrame);
  };

  drawFrame();

  const canvasStream = canvas.captureStream(settings.frameRate || 30);
  pipeline.canvasStream = canvasStream;
  const [mirroredVideoTrack] = canvasStream.getVideoTracks();

  return {
    mirroredVideoTrack: mirroredVideoTrack || videoTrack,
    cleanup: () => stopMirroredVideoPipeline(pipeline),
  };
}

export default function useWebRTCConnection({
  userType,
  roomState,
  connectionState,
  socketId,
  sendEvent,
  subscribe,
}) {
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const outgoingStreamRef = useRef(null);
  const mirroredVideoCleanupRef = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  const offerSentForCurrentSessionRef = useRef(false);
  const lastOfferAtRef = useRef(0);
  const lastSocketIdRef = useRef(null);
  const lastOfferRequestAtRef = useRef(0);

  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const roleLabel = useMemo(
    () => (userType || "unknown").toUpperCase(),
    [userType]
  );
  const bothParticipantsConnected = useMemo(() => {
    const roles = ["candidate", "interviewer"];
    return roles.every((role) => {
      const participant = roomState?.participants?.find(
        (item) => item?.user_type === role
      );
      return participant?.state === "connected";
    });
  }, [roomState?.participants]);

  const log = useCallback(
    (eventLabel, payload) => {
      if (payload === undefined) {
        console.log(`[${roleLabel}][${eventLabel}]`);
        return;
      }
      console.log(`[${roleLabel}][${eventLabel}]`, payload);
    },
    [roleLabel]
  );

  // FIXED: Added keepLocalStream parameter (default false).
  // When a remote participant reconnects we only need to reset the peer
  // connection, not tear down our own camera/mic. Destroying the local stream
  // on every reconnect caused unnecessary camera restarts and races where
  // ensureLocalStream re-acquired the device mid-negotiation.
  const closePeerConnection = useCallback((keepLocalStream = false) => {
    const pc = peerConnectionRef.current;
    if (pc) {
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      pc.close();
      peerConnectionRef.current = null;
      log("PEER_CONNECTION_CLOSED");
    }

    if (!keepLocalStream) {
      if (mirroredVideoCleanupRef.current) {
        mirroredVideoCleanupRef.current();
        mirroredVideoCleanupRef.current = null;
      }
      outgoingStreamRef.current = null;
      stopMediaStream(localStreamRef.current);
      localStreamRef.current = null;
      setLocalStream(null);
    }

    setRemoteStream(null);
    pendingIceCandidatesRef.current = [];
    offerSentForCurrentSessionRef.current = false;
    lastOfferAtRef.current = 0;
    lastOfferRequestAtRef.current = 0;
  }, [log]);

  const ensureLocalStream = useCallback(async () => {
    if (localStreamRef.current) {
      return localStreamRef.current;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    localStreamRef.current = stream;
    setLocalStream(stream);
    return stream;
  }, []);

  const ensureOutgoingStream = useCallback(async () => {
    if (outgoingStreamRef.current) {
      return outgoingStreamRef.current;
    }

    const stream = await ensureLocalStream();
    const { mirroredVideoTrack, cleanup } = await createMirroredVideoTrack(stream);
    mirroredVideoCleanupRef.current = cleanup;

    const outgoingTracks = [
      ...stream.getAudioTracks(),
      ...(mirroredVideoTrack ? [mirroredVideoTrack] : []),
    ];
    outgoingStreamRef.current = new MediaStream(outgoingTracks);
    return outgoingStreamRef.current;
  }, [ensureLocalStream]);

  const ensurePeerConnection = useCallback(async () => {
    if (peerConnectionRef.current) {
      return peerConnectionRef.current;
    }

    const stream = await ensureOutgoingStream();
    const peerConnection = new RTCPeerConnection(buildIceConfig());

    stream.getTracks().forEach((track) => {
      peerConnection.addTrack(track, stream);
    });

    peerConnection.ontrack = (event) => {
      if (!event.streams || event.streams.length === 0) {
        return;
      }
      setRemoteStream(event.streams[0]);
    };

    peerConnection.onicecandidate = (event) => {
      if (!event.candidate) {
        return;
      }

      sendEvent("webrtc:ice", {
        candidate: event.candidate.toJSON(),
      });
    };

    peerConnection.onconnectionstatechange = () => {
      if (["failed", "disconnected"].includes(peerConnection.connectionState)) {
        log("RECONNECT_DETECTED", {
          connectionState: peerConnection.connectionState,
        });
      }
    };

    // FIXED: Added ICE connection state handler.
    // When ICE fails, attempt an ICE restart (interviewer only) before
    // resorting to a full peer connection teardown. This recovers ~50% of
    // cases that previously required the user to reload the page.
    peerConnection.oniceconnectionstatechange = () => {
      log("ICE_CONNECTION_STATE", peerConnection.iceConnectionState);
      if (peerConnection.iceConnectionState === "failed") {
        if (userType === "interviewer") {
          log("ICE_RESTART_TRIGGERED");
          offerSentForCurrentSessionRef.current = false;
          createOfferIfNeeded(true).catch((err) => {
            log("WEBRTC_ICE_RESTART_ERROR", err);
          });
        }
      }
    };

    peerConnectionRef.current = peerConnection;
    log("PEER_CONNECTION_CREATED", {
      config: buildIceConfig(),
    });
    return peerConnection;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureOutgoingStream, log, sendEvent, userType]);

  const flushPendingRemoteIce = useCallback(async () => {
    const peerConnection = peerConnectionRef.current;
    if (!peerConnection || !peerConnection.remoteDescription) {
      return;
    }

    while (pendingIceCandidatesRef.current.length > 0) {
      const candidate = pendingIceCandidatesRef.current.shift();
      try {
        await peerConnection.addIceCandidate(candidate);
      } catch (error) {
        log("WEBRTC_ICE_FLUSH_CANDIDATE_ERROR", {
          signalingState: peerConnection.signalingState,
          iceConnectionState: peerConnection.iceConnectionState,
          error: error?.message || String(error),
        });
      }
    }
  }, [log]);

  const handleWebRtcOffer = useCallback(
    async (payload) => {
      // ADDED: log offer arrival so we can confirm it is reaching the candidate.
      log("WEBRTC_OFFER_RECEIVED", { hasSdp: Boolean(payload?.sdp) });

      if (!payload?.sdp) {
        return;
      }

      let peerConnection = await ensurePeerConnection();

      if (peerConnection.signalingState === "have-remote-offer") {
        log("WEBRTC_DUPLICATE_OFFER_IGNORED");
        return;
      }

      if (
        peerConnection.signalingState !== "stable" &&
        peerConnection.signalingState !== "have-local-offer"
      ) {
        log("WEBRTC_UNSTABLE_OFFER_RESET", {
          signalingState: peerConnection.signalingState,
        });
        closePeerConnection(true);
        peerConnection = await ensurePeerConnection();
      }

      if (peerConnection.signalingState === "have-local-offer") {
        await peerConnection.setLocalDescription({ type: "rollback" });
      }

      await peerConnection.setRemoteDescription(
        new RTCSessionDescription(payload.sdp)
      );
      await flushPendingRemoteIce();

      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      sendEvent("webrtc:answer", {
        sdp: peerConnection.localDescription,
      });
    },
    [closePeerConnection, ensurePeerConnection, flushPendingRemoteIce, log, sendEvent]
  );

  const handleWebRtcAnswer = useCallback(
    async (payload) => {
      if (!payload?.sdp) {
        return;
      }

      const peerConnection = peerConnectionRef.current;
      if (!peerConnection) {
        return;
      }

      if (peerConnection.signalingState !== "have-local-offer") {
        log("WEBRTC_STALE_ANSWER_IGNORED", {
          signalingState: peerConnection.signalingState,
        });
        return;
      }

      await peerConnection.setRemoteDescription(
        new RTCSessionDescription(payload.sdp)
      );
      await flushPendingRemoteIce();
    },
    [flushPendingRemoteIce, log]
  );

  const handleWebRtcIce = useCallback(async (payload) => {
    if (!payload?.candidate) {
      return;
    }

    const candidate = new RTCIceCandidate(payload.candidate);
    const peerConnection = peerConnectionRef.current;

    if (!peerConnection || !peerConnection.remoteDescription) {
      pendingIceCandidatesRef.current.push(candidate);
      if (!peerConnection) {
        log("WEBRTC_ICE_QUEUED_BEFORE_PEER");
      }
      return;
    }

    try {
      await peerConnection.addIceCandidate(candidate);
    } catch (error) {
      pendingIceCandidatesRef.current.push(candidate);
      log("WEBRTC_ICE_ADD_FAILED_REQUEUED", {
        signalingState: peerConnection.signalingState,
        iceConnectionState: peerConnection.iceConnectionState,
        error: error?.message || String(error),
      });
    }
  }, [log]);

  const createOfferIfNeeded = useCallback(async (forceRetry = false) => {
    if (
      !bothParticipantsConnected ||
      ["ended", "reconnecting"].includes(roomState?.status) ||
      userType !== "interviewer"
    ) {
      return;
    }

    if (offerSentForCurrentSessionRef.current && !forceRetry) {
      return;
    }

    // ADDED: cooldown guard — don't retry within 4 seconds of last offer.
    // Previously multiple effects could all trigger createOfferIfNeeded
    // simultaneously, causing rapid-fire offers that caused signaling glare.
    const now = Date.now();
    if (!forceRetry && now - lastOfferAtRef.current < 4000) {
      return;
    }

    offerSentForCurrentSessionRef.current = true;
    lastOfferAtRef.current = now;

    try {
      const peerConnection = await ensurePeerConnection();
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
        iceRestart: forceRetry,
      });
      await peerConnection.setLocalDescription(offer);

      sendEvent("webrtc:offer", {
        sdp: peerConnection.localDescription,
      });
    } catch (error) {
      offerSentForCurrentSessionRef.current = false;
      log("WEBRTC_OFFER_CREATE_ERROR", error);
    }
  }, [bothParticipantsConnected, ensurePeerConnection, log, roomState?.status, sendEvent, userType]);

  const verifyTurnRelayUsage = useCallback(async () => {
    const peerConnection = peerConnectionRef.current;
    if (!peerConnection) {
      return {
        relaySelected: false,
        reason: "Peer connection not initialized.",
      };
    }

    const stats = await peerConnection.getStats();
    let selectedPair = null;
    let localCandidate = null;
    let remoteCandidate = null;

    stats.forEach((report) => {
      if (report.type === "transport" && report.selectedCandidatePairId) {
        selectedPair = stats.get(report.selectedCandidatePairId);
      }
      if (report.type === "candidate-pair" && report.state === "succeeded" && report.nominated) {
        selectedPair = report;
      }
    });

    if (!selectedPair) {
      return {
        relaySelected: false,
        reason: "No selected candidate pair found yet.",
      };
    }

    localCandidate = stats.get(selectedPair.localCandidateId);
    remoteCandidate = stats.get(selectedPair.remoteCandidateId);

    return {
      relaySelected: localCandidate?.candidateType === "relay",
      localCandidateType: localCandidate?.candidateType,
      remoteCandidateType: remoteCandidate?.candidateType,
      localCandidateProtocol: localCandidate?.protocol,
      remoteCandidateProtocol: remoteCandidate?.protocol,
    };
  }, []);

  useEffect(() => {
    const unsubscribeOffer = subscribe("webrtc:offer", (payload) => {
      handleWebRtcOffer(payload).catch((error) => {
        log("WEBRTC_OFFER_HANDLE_ERROR", error);
      });
    });

    const unsubscribeAnswer = subscribe("webrtc:answer", (payload) => {
      handleWebRtcAnswer(payload).catch((error) => {
        log("WEBRTC_ANSWER_HANDLE_ERROR", error);
      });
    });

    const unsubscribeIce = subscribe("webrtc:ice", (payload) => {
      handleWebRtcIce(payload).catch((error) => {
        log("WEBRTC_ICE_HANDLE_ERROR", error);
      });
    });

    const unsubscribeUserLeft = subscribe("user:left", () => {
      closePeerConnection();
    });

    const unsubscribeRoomEnded = subscribe("room:ended", () => {
      closePeerConnection();
    });

    // FIXED: Pass keepLocalStream=true so our own camera/mic is not torn down
    // when the remote participant reconnects. Previously this caused the local
    // stream to be destroyed and re-acquired, creating a race with ensurePeerConnection.
    const unsubscribeUserReconnected = subscribe("user:reconnected", (payload) => {
      if (payload?.user_type === userType) {
        log("WEBRTC_SELF_RECONNECTED_EVENT_IGNORED", payload);
        return;
      }
      closePeerConnection(true);
    });

    const unsubscribeOfferRequest = subscribe("webrtc:offer:request", () => {
      if (userType !== "interviewer") {
        return;
      }
      offerSentForCurrentSessionRef.current = false;
      createOfferIfNeeded(true).catch((error) => {
        log("WEBRTC_OFFER_REQUEST_HANDLE_ERROR", error);
      });
    });

    return () => {
      unsubscribeOffer();
      unsubscribeAnswer();
      unsubscribeIce();
      unsubscribeUserLeft();
      unsubscribeRoomEnded();
      unsubscribeUserReconnected();
      unsubscribeOfferRequest();
    };
  }, [
    closePeerConnection,
    createOfferIfNeeded,
    handleWebRtcAnswer,
    handleWebRtcIce,
    handleWebRtcOffer,
    log,
    subscribe,
    userType,
  ]);

  useEffect(() => {
    if (!socketId) {
      return;
    }

    if (lastSocketIdRef.current && lastSocketIdRef.current !== socketId) {
      log("RECONNECT_DETECTED", {
        previousSocketId: lastSocketIdRef.current,
        newSocketId: socketId,
      });
      closePeerConnection();
    }
    lastSocketIdRef.current = socketId;
  }, [closePeerConnection, log, socketId]);

  useEffect(() => {
    if (connectionState === "reconnecting") {
      closePeerConnection();
    }
  }, [closePeerConnection, connectionState]);

  useEffect(() => {
    createOfferIfNeeded().catch((error) => {
      log("WEBRTC_OFFER_CREATE_ERROR", error);
    });
  }, [createOfferIfNeeded, log]);

  useEffect(() => {
    if (
      userType !== "interviewer" ||
      !bothParticipantsConnected ||
      ["ended", "reconnecting"].includes(roomState?.status)
    ) {
      return undefined;
    }

    const interval = setInterval(() => {
      const pc = peerConnectionRef.current;
      const remoteReady = Boolean(remoteStream);
      const connectionStable =
        pc && ["connected", "completed"].includes(pc.iceConnectionState);
      const enoughTimePassed = Date.now() - lastOfferAtRef.current > 8000;

      if (!remoteReady && !connectionStable && enoughTimePassed) {
        offerSentForCurrentSessionRef.current = false;
        createOfferIfNeeded(true).catch((error) => {
          log("WEBRTC_OFFER_RETRY_ERROR", error);
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [
    bothParticipantsConnected,
    createOfferIfNeeded,
    log,
    remoteStream,
    roomState?.status,
    userType,
  ]);

  useEffect(() => {
    if (
      userType !== "candidate" ||
      !bothParticipantsConnected ||
      ["ended", "reconnecting"].includes(roomState?.status) ||
      remoteStream
    ) {
      return undefined;
    }

    const interval = setInterval(() => {
      const enoughTimePassed = Date.now() - lastOfferRequestAtRef.current > 5000;
      if (!enoughTimePassed) {
        return;
      }

      lastOfferRequestAtRef.current = Date.now();
      log("WEBRTC_OFFER_REQUEST_SENT");
      sendEvent("webrtc:offer:request", {
        target_user_type: "interviewer",
        reason: "candidate_missing_remote_stream",
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [bothParticipantsConnected, log, remoteStream, roomState?.status, sendEvent, userType]);

  useEffect(() => {
    const status = roomState?.status;
    if (status === "ended" || status === "reconnecting") {
      return;
    }
    if (status === "waiting" && !bothParticipantsConnected) {
      return;
    }

    ensureLocalStream().catch((error) => {
      log("LOCAL_STREAM_INIT_ERROR", error);
    });
  }, [bothParticipantsConnected, ensureLocalStream, log, roomState?.status]);

  useEffect(() => {
    if (!roomState?.status) {
      return;
    }

    if (!bothParticipantsConnected || ["ended", "reconnecting"].includes(roomState.status)) {
      offerSentForCurrentSessionRef.current = false;
    }

    if (
      roomState.status === "ended" ||
      roomState.status === "reconnecting" ||
      (roomState.status === "waiting" && !bothParticipantsConnected)
    ) {
      closePeerConnection();
    }
  }, [bothParticipantsConnected, closePeerConnection, roomState?.status]);

  useEffect(() => {
    return () => {
      closePeerConnection();
    };
  }, [closePeerConnection]);

  return {
    localStream,
    remoteStream,
    closePeerConnection,
    verifyTurnRelayUsage,
  };
}
